import { useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import type { Trip } from '@/services/trips';
import { applePlatformFlags } from '@/config/applePlatformFlags';
import { pickAppleTripSurfaceTrip, syncAppleTripSurfaceSnapshot } from '@/services/appleTripSurfaceSync';

const TRIPS_SURFACE_SYNC_DEBOUNCE_MS = 2_000;

export type UseAppleTripSurfaceTripsSyncOpts = {
  trips: Trip[];
  userId?: string | null;
  enabled?: boolean;
};

/**
 * Keeps the home-screen widget snapshot in sync with live Trips tab data.
 * Background cache-only sync misses collaborator trips until Plan is opened.
 */
export const useAppleTripSurfaceTripsSync = (opts: UseAppleTripSurfaceTripsSyncOpts): void => {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tripsSignature = useMemo(
    () =>
      opts.trips
        .map((trip) => {
          const start =
            trip.startDate instanceof Date ? trip.startDate.getTime() : new Date(trip.startDate).getTime();
          const end =
            trip.endDate instanceof Date ? trip.endDate.getTime() : new Date(trip.endDate).getTime();
          return `${trip.id}:${start}:${end}:${trip.isArchived ? 1 : 0}`;
        })
        .join('|'),
    [opts.trips],
  );

  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    if (!applePlatformFlags.phase5Enabled || !applePlatformFlags.widgetNextTripEnabled) return;
    if (!opts.enabled || !opts.userId || opts.trips.length === 0) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const surfaceTrip = pickAppleTripSurfaceTrip({
        trips: opts.trips,
        userId: opts.userId,
      });
      void syncAppleTripSurfaceSnapshot({
        trips: opts.trips,
        userId: opts.userId,
        selectedTrip: surfaceTrip,
        selectedItineraryItems: [],
      }).catch(() => {
        // Best-effort widget refresh from Trips tab.
      });
    }, TRIPS_SURFACE_SYNC_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [opts.enabled, opts.trips, opts.userId, tripsSignature]);
};
